import { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Modal, Text, TextButton, PrimaryButton, Autocomplete, Field, Alert, IconButton } from '@artprov/design-system'
import FormDropdown from './FormDropdown'
const CUIT_REGEX = /^\d{2}-\d{8}-\d$/

export default function ModalAsociarCuenta({
  open,
  setOpen,
  onCancel,
  onSubmit,
  isSubmitting = false,
  cargoOptions = [],
  areaOptions = [],
}) {
  const [form, setForm] = useState({
    cuit: '',
    cargo: '',
    cargoId: '',
    areaAfinidad: '',
    areaAfinidadId: '',
  })

  const [cargoSearchValue, setCargoSearchValue] = useState('')

  const handleCargoChange = useCallback((opcion) => {
    const id = opcion?.id ?? ''
    const descripcion = opcion?.descripcion ?? ''
    setForm((prev) => ({
      ...prev,
      cargo: descripcion,
      cargoId: id,
    }))
    setCargoSearchValue(descripcion)
  }, [])

  const handleAreaChange = useCallback((option) => {
    setForm((prev) => ({
      ...prev,
      areaAfinidad: option.label,
      areaAfinidadId: option.value,
    }))
  }, [])

  const handleCuitChange = useCallback((value) => {
    setForm((prev) => ({ ...prev, cuit: value }))
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
    setForm({
      cuit: '',
      cargo: '',
      cargoId: '',
      areaAfinidad: '',
      areaAfinidadId: '',
    })
    setCargoSearchValue('')
  }, [setOpen])

  const handleCancel = useCallback(() => {
    handleClose()
    onCancel?.()
  }, [handleClose, onCancel])

  const handleSubmit = useCallback(() => {
    if (isSubmitting) return
    // El cierre del modal lo maneja el padre tras la respuesta del backend
    onSubmit?.(form)
  }, [form, onSubmit, isSubmitting])

  // Resetear el formulario cuando el modal se cierra (incluye el cierre
  // automático que hace el padre tras una solicitud exitosa)
  useEffect(() => {
    if (!open) {
      setForm({
        cuit: '',
        cargo: '',
        cargoId: '',
        areaAfinidad: '',
        areaAfinidadId: '',
      })
      setCargoSearchValue('')
    }
  }, [open])

  const isCuitValid = useMemo(() => {
    if (!form.cuit || form.cuit.trim().length === 0) {
      return false
    }
    return CUIT_REGEX.test(form.cuit)
  }, [form.cuit])

  const isFormValid = isCuitValid && form.cargoId && form.areaAfinidadId

  return (
    <Modal.Container open={open} setOpen={setOpen}>
      <Modal.Content width="min(95vw, 560px)" height="auto" style={{ maxHeight: '90vh', overflow: 'visible' }}>
        <div className="py-3 px-2 max-w-full max-h-[85vh] overflow-y-auto">
          <div className="flex justify-between items-start mb-4">
            <Text variant="h6" className="text-primary-10 text-lg sm:text-xl">
              Asociar nueva cuenta
            </Text>
            <IconButton icon="close" size="large" className="w-5 h-5" onClick={handleClose} aria-label="Cerrar modal" />
          </div>

          <div className="mb-6">
            <Alert
              variant="warning"
              iconStyle="hidden"
              title=""
              description={
                'La asociación requerirá la aprobación del Administrador.\nTe notificaremos por correo electrónico.'
              }
              descriptionStyle="font-semibold whitespace-pre-line"
              interactive={false}
              buttonText=""
              containerStyle="!max-w-none w-full [&>div:first-child>svg]:hidden"
            />
          </div>

          <div className="mb-4">
            <Field
              variant="cuil"
              label="CUIT"
              placeholder="Ej.: 20-23456789-3"
              value={form.cuit}
              onChange={handleCuitChange}
              startIcon=""
              endIcon=""
              fullWidth
              hints={[]}
            />
          </div>

          <div className="mb-4">
            <Autocomplete
              label="Cargo"
              placeholder="Seleccionar"
              opciones={cargoOptions.map((opt) => ({
                id: opt.value,
                descripcion: opt.label,
              }))}
              searchValue={cargoSearchValue}
              setSearchValue={setCargoSearchValue}
              setSelected={handleCargoChange}
              hints={[]}
              classNameContainer="w-full"
            />
          </div>

          <div className="mb-6">
            <FormDropdown
              label="Área de afinidad"
              placeholder="Seleccionar"
              value={form.areaAfinidad}
              options={areaOptions}
              onSelect={handleAreaChange}
              onBlur={() => {}}
            />
          </div>

          <div className="flex flex-col-reverse sm:flex-row justify-end gap-4">
            <TextButton
              onClick={handleCancel}
              size="small"
              descriptionTextVariant="subtitle1"
              className="!h-[34px] !min-h-[34px] !px-3 whitespace-nowrap inline-flex items-center justify-center w-full sm:w-auto"
            >
              Cancelar
            </TextButton>

            <PrimaryButton
              onClick={handleSubmit}
              size="small"
              disabled={!isFormValid || isSubmitting}
              className="!h-[34px] !min-h-[34px] !px-4 whitespace-nowrap inline-flex items-center justify-center w-full sm:w-auto"
            >
              {isSubmitting ? 'Enviando...' : 'Solicitar asociación'}
            </PrimaryButton>
          </div>
        </div>
      </Modal.Content>
    </Modal.Container>
  )
}

ModalAsociarCuenta.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  cargoOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  areaOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
}
